import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { calculateLinestringDistance, formatDistance } from '../utils/geometryUtils';
import feedingService from '../services/feedingService';
import { hopperOverfillWarning } from '../utils/hopperCapacity';
import { useToast } from '../../../components/ui/ToastProvider';

/**
 * FeatureTooltip - Handles tooltips for map features using Mapbox's native popup system
 * 
 * @param {Object} map - Mapbox map instance
 * @param {Array} features - Array of features to show tooltips for
 * @param {('personal'|'club')} mapType - Current map type; suppress polygon acreage on 'club'
 */
export function FeatureTooltip({ map, features, mapType = 'personal', canEnableFeed = true }) {
  const popupRef = useRef(null);
  const { show } = useToast();
  const shownPolygonIdRef = useRef(null);
  const hideTimerRef = useRef(null);
  const pendingIdRef = useRef(null);
  const switchTimerRef = useRef(null);
  const polygonActiveRef = useRef(false);

  useEffect(() => {
    if (!map || !features || features.length === 0) return;

    // Create popup instance
    popupRef.current = new mapboxgl.Popup({
      closeButton: false,
      className: 'feature-tooltip',
      maxWidth: '300px'
    });

    // Helper function to format tooltip content
    const getTooltipContent = (feature) => {
      const featureData = features.find(f => f.id === feature.id);
      if (!featureData) return null;

      if (featureData.type === 'polygon' && featureData.acreage) {
        return `<strong>${featureData.acreage.toFixed(2)} acres</strong>`;
      } else if (featureData.type === 'line') {
        // Prefer backend-computed length; else compute client-side
        let lengthMiles = featureData.length;
        if (!lengthMiles && featureData.geojson && featureData.geojson.type === 'LineString') {
          try {
            lengthMiles = calculateLinestringDistance(featureData.geojson.coordinates);
          } catch (_) {}
        }
        if (lengthMiles) {
          return `<strong>Distance: ${formatDistance(lengthMiles)}</strong>`;
        }
      } else if (featureData.type === 'point') {
        const lines = [];
        if (featureData.name) lines.push(`<strong>${featureData.name}</strong>`);
        if (featureData.optimal_winds && Array.isArray(featureData.optimal_winds) && featureData.optimal_winds.length > 0) {
          lines.push(`Optimal winds: ${featureData.optimal_winds.join(', ')}`);
        } else if (featureData.optimal_wind !== undefined) {
          const label = (featureData.optimal_wind || '') === '' ? 'No preference' : featureData.optimal_wind;
          lines.push(`Optimal wind: ${label}`);
        }
        if (featureData.percent_remaining != null || featureData.estimated_remaining_lbs != null) {
          const pill = featureData.percent_remaining != null ? `${featureData.percent_remaining}%` : `${featureData.estimated_remaining_lbs || 0} lbs`;
          const eta = featureData.refill_due_date ? new Date(featureData.refill_due_date).toLocaleDateString() : '';
          lines.push(`<div style="margin-top:4px"><span style="padding:2px 6px;border:1px solid var(--color-border);border-radius:12px;background:var(--color-surface-muted);font-size:12px">${pill}${eta ? ` • ETA ${eta}` : ''}</span></div>`);
          const overfill = hopperOverfillWarning({
            amountLbs: 50,
            capacityLbs: featureData.feed_capacity_lbs,
            remainingLbs: featureData.estimated_remaining_lbs,
          });
          if (overfill) {
            lines.push(`<div style="margin-top:6px;font-size:12px;color:var(--color-danger, #a44)">${overfill}</div>`);
          } else if (canEnableFeed === true) {
            lines.push(`<div style="margin-top:6px"><a href="#" class="y-add-feed" data-feature-id="${feature.id}" style="font-size:12px">+50 lbs</a></div>`);
          }
        }
        return lines.join('<br/>') || null;
      }
      
      return null;
    };

    // Helper to show a polygon tooltip once and auto-hide
    const showPolygonOnce = (polygonId, lngLat) => {
      if (shownPolygonIdRef.current === polygonId && popupRef.current && popupRef.current.isOpen()) {
        return;
      }
      shownPolygonIdRef.current = polygonId;
      polygonActiveRef.current = true;
      if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
      const content = getTooltipContent({ id: polygonId });
      if (content) {
        popupRef.current
          .setLngLat(lngLat)
          .setHTML(content)
          .addTo(map);
      }
      hideTimerRef.current = setTimeout(() => {
        try { popupRef.current.remove(); } catch (_) {}
        hideTimerRef.current = null;
        // Keep shownPolygonIdRef so it doesn't re-show until mouseleave
        polygonActiveRef.current = false;
      }, 3500);
    };

    const schedulePolygonShow = (polygonId, lngLat) => {
      if (pendingIdRef.current === polygonId) return;
      pendingIdRef.current = polygonId;
      if (switchTimerRef.current) { clearTimeout(switchTimerRef.current); switchTimerRef.current = null; }
      switchTimerRef.current = setTimeout(() => {
        showPolygonOnce(polygonId, lngLat);
        pendingIdRef.current = null;
        switchTimerRef.current = null;
      }, 75);
    };

    // One-time acreage tooltip on polygon hover with auto-hide and nested polygon switching
    const handlePolygonMouseMove = (e) => {
      try {
        // Determine topmost polygon feature under cursor (Mapbox returns topmost first)
        const featuresAtPoint = map.queryRenderedFeatures(e.point, { layers: ['features-polygons'] }) || [];
        if (!featuresAtPoint.length) return;
        const top = featuresAtPoint[0];
        const polygonId = top && (top.id || top.properties?.id);
        if (!polygonId) return;

        if (!(shownPolygonIdRef.current === polygonId && popupRef.current && popupRef.current.isOpen())) {
          schedulePolygonShow(polygonId, e.lngLat);
        }
      } catch (_) {}
    };

    const handlePolygonMouseLeave = () => {
      // Clear timer and popup; allow re-show when re-entering
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (switchTimerRef.current) {
        clearTimeout(switchTimerRef.current);
        switchTimerRef.current = null;
      }
      try { popupRef.current.remove(); } catch (_) {}
      shownPolygonIdRef.current = null;
      pendingIdRef.current = null;
      polygonActiveRef.current = false;
    };

    // Setup tooltip for lines
    const handleLineMouseEnter = (e) => {
      if (polygonActiveRef.current) return; // suppress while polygon tooltip is active
      const content = getTooltipContent(e.features[0]);
      if (content) {
        popupRef.current
          .setLngLat(e.lngLat)
          .setHTML(content)
          .addTo(map);
      }
    };

    const handleLineMouseLeave = () => {
      popupRef.current.remove();
    };

    // Setup tooltip for points
    const handlePointMouseEnter = (e) => {
      if (polygonActiveRef.current) return; // suppress while polygon tooltip is active
      const content = getTooltipContent(e.features[0]);
      if (content) {
        popupRef.current
          .setLngLat(e.lngLat)
          .setHTML(content)
          .addTo(map);
        // Bind quick add feed if present
        try {
          const el = popupRef.current.getElement ? popupRef.current.getElement() : popupRef.current._content;
          if (el) {
            const btn = el.querySelector && el.querySelector('a.y-add-feed');
            if (btn) {
              btn.addEventListener('click', async (ev) => {
                ev.preventDefault(); ev.stopPropagation();
                try {
                  if (canEnableFeed !== true) return;
                  const idVal = e.features[0].id;
                  let target = null;
                  if (mapType === 'club') {
                    const numericId = String(idVal).startsWith('location-') ? Number(String(idVal).split('-')[1]) : Number(idVal);
                    target = { kind: 'club', id: numericId };
                  } else {
                    target = { kind: 'personal', id: Number(idVal) };
                  }
                  await feedingService.addFeedEvent(target, { amount_lbs: 50 });
                  try { btn.textContent = 'Added +50'; btn.style.pointerEvents = 'none'; } catch(_) {}
                  try { show({ title: 'Feed logged', body: '+50 lbs added.', variant: 'success' }); } catch(_) {}
                } catch (_) {}
              }, { once: true });
            }
          }
        } catch (_) {}
      }
    };

    const handlePointMouseLeave = () => {
      popupRef.current.remove();
    };

    // Add event listeners
    // Only bind polygon acreage tooltip for personal maps
    if (mapType !== 'club') {
      map.on('mousemove', 'features-polygons', handlePolygonMouseMove);
      map.on('mouseleave', 'features-polygons', handlePolygonMouseLeave);
      // Also handle outline so boundary hover behaves consistently
      map.on('mousemove', 'features-polygons-outline', handlePolygonMouseMove);
      map.on('mouseleave', 'features-polygons-outline', handlePolygonMouseLeave);
    }
    
    map.on('mouseenter', 'features-lines', handleLineMouseEnter);
    map.on('mouseleave', 'features-lines', handleLineMouseLeave);
    
    map.on('mouseenter', 'features-points', handlePointMouseEnter);
    map.on('mouseleave', 'features-points', handlePointMouseLeave);

    // Cleanup function
    return () => {
      if (mapType !== 'club') {
        map.off('mousemove', 'features-polygons', handlePolygonMouseMove);
        map.off('mouseleave', 'features-polygons', handlePolygonMouseLeave);
        map.off('mousemove', 'features-polygons-outline', handlePolygonMouseMove);
        map.off('mouseleave', 'features-polygons-outline', handlePolygonMouseLeave);
      }
      
      map.off('mouseenter', 'features-lines', handleLineMouseEnter);
      map.off('mouseleave', 'features-lines', handleLineMouseLeave);
      
      map.off('mouseenter', 'features-points', handlePointMouseEnter);
      map.off('mouseleave', 'features-points', handlePointMouseLeave);
      
      if (popupRef.current) { popupRef.current.remove(); }
      if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); }
      if (switchTimerRef.current) { clearTimeout(switchTimerRef.current); }
    };
  }, [map, features, mapType, show, canEnableFeed]);

  // No DOM elements to render
  return null;
}
